/**
 * Cross-mount memoization for the expensive report compute functions.
 *
 * Each report page is mounted/unmounted as the user navigates between the
 * reports hub and a report (and back). React throws away `useMemo` caches on
 * unmount, so without this every navigation re-runs the full report
 * computation (`computeXxxReport`, cost-price enrichment, …) even though the
 * underlying store data hasn't changed.
 *
 * `cachedReportValue` is a drop-in replacement for `useMemo` that keeps the
 * computed value at MODULE scope keyed by `cacheKey`. The store data arrays
 * (orders / revenue / cost prices / counts) and the shared time-control Dates
 * are referentially stable across navigations, so re-entering a report with
 * unchanged inputs is a cache hit and returns instantly. It is recomputed only
 * when the underlying data or the selected period actually changes — exactly
 * the same dependency semantics as `useMemo` (shallow `Object.is` compare).
 *
 * It is a plain function (not a hook): it reads/writes module state and calls
 * no React hooks, so it is safe to call during render and returns a stable
 * reference for unchanged deps.
 */

interface CacheEntry {
  deps: readonly unknown[];
  value: unknown;
}

// A few entries per key so that toggling the time period back and forth (e.g.
// month → week → month) also stays cached, not just navigating away and back.
const MAX_ENTRIES_PER_KEY = 4;

const cache = new Map<string, CacheEntry[]>();

function depsEqual(a: readonly unknown[], b: readonly unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return false;
  }
  return true;
}

export function cachedReportValue<T>(cacheKey: string, deps: readonly unknown[], factory: () => T): T {
  let entries = cache.get(cacheKey);
  if (!entries) {
    entries = [];
    cache.set(cacheKey, entries);
  }

  const hitIndex = entries.findIndex((e) => depsEqual(e.deps, deps));
  if (hitIndex !== -1) {
    // Move the hit to the front (most-recently-used).
    const [hit] = entries.splice(hitIndex, 1);
    entries.unshift(hit);
    return hit.value as T;
  }

  const value = factory();
  entries.unshift({ deps, value });
  if (entries.length > MAX_ENTRIES_PER_KEY) entries.length = MAX_ENTRIES_PER_KEY;
  return value;
}
